import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Shared form validators for the application.
 * Centralizes validation rules for phone, email, document number
 * and other common fields used across sample entry forms.
 */

/**
 * Colombian phone number pattern: 10 digits, starts with 3 (mobile) or region codes.
 * Accepts only digits, min 7 max 10 characters.
 */
const PHONE_PATTERN = /^\d{7,10}$/;

/**
 * Standard email regex per RFC 5322 simplified.
 */
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

/**
 * Numeric-only pattern (for document numbers, patient numbers, etc.)
 */
const NUMERIC_ONLY_PATTERN = /^\d+$/;

/**
 * Validator for Colombian phone numbers.
 * Allows 7-10 digits only (landline 7 digits, mobile 10 digits).
 * Returns { phoneInvalid: true } if invalid.
 */
export function colombianPhoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const value = String(control.value).trim();
    if (!PHONE_PATTERN.test(value)) {
      return {
        phoneInvalid: {
          message: 'Ingrese un número de teléfono válido (7-10 dígitos)'
        }
      };
    }
    return null;
  };
}

/**
 * Validator for email format.
 * Returns { email: true } to be consistent with Angular's built-in Validators.email error key.
 */
export function emailFormatValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const value = String(control.value).trim();
    if (!EMAIL_PATTERN.test(value)) {
      return { email: true };
    }
    return null;
  };
}

/**
 * Validator that ensures a value contains only digits.
 * Useful for document number, patient number fields.
 * Returns { numericOnly: true } if non-numeric characters are found.
 */
export function numericOnlyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const value = String(control.value).trim();
    if (!NUMERIC_ONLY_PATTERN.test(value)) {
      return { numericOnly: { message: 'Solo se permiten números' } };
    }
    return null;
  };
}

/**
 * Max date validator preventing future dates (for birthDate).
 * Returns { maxDate: { value, message } } if the date is in the future.
 */
export function maxDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const inputDate = new Date(control.value);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (inputDate > today) {
      return {
        maxDate: {
          value: control.value,
          message: 'La fecha de nacimiento no puede ser futura'
        }
      };
    }
    return null;
  };
}

/**
 * Combines required + phone validators for fields that need both.
 */
export function requiredPhoneValidators(): ValidatorFn[] {
  return [Validators.required, colombianPhoneValidator()];
}

/**
 * Optional phone validators (no required, but if filled must be valid format).
 */
export function optionalPhoneValidators(): ValidatorFn[] {
  return [colombianPhoneValidator()];
}

/**
 * Optional email validators (no required, but if filled must be valid format).
 */
export function optionalEmailValidators(): ValidatorFn[] {
  return [emailFormatValidator()];
}

/**
 * Required date validators with future-date prevention.
 */
export function requiredDateValidators(): ValidatorFn[] {
  return [Validators.required, maxDateValidator()];
}
